import React, { useState, useEffect } from 'react';
import { api } from '../../utils/api';
import { Clock, AlertTriangle, Moon, Sun } from 'lucide-react';
import { fmtTime } from '../../utils/dateTime';

const WorkerHistory = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getClockHistory().then(res => setEntries(res.data || [])).catch(console.error).finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center h-48">
      <div className="w-6 h-6 border-2 border-[hsl(16,100%,55%)] border-t-transparent op-round rounded-full animate-spin" />
    </div>
  );

  const totalHours = entries.reduce((s, e) => s + (e.total_hours || 0), 0);
  const thisMonth = entries.filter(e => {
    const d = new Date(e.date);
    const now = new Date();
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  });
  const monthHours = thisMonth.reduce((s, e) => s + (e.total_hours || 0), 0);

  return (
    <div className="space-y-4 pb-20" data-testid="worker-history-page">
      {/* Summary */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-card border border-border rounded p-4">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">Denna månad</p>
          <p className="text-2xl font-bold text-foreground mt-1">{monthHours.toFixed(1)}h</p>
        </div>
        <div className="bg-card border border-border rounded p-4">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">Totalt</p>
          <p className="text-2xl font-bold text-foreground mt-1">{totalHours.toFixed(1)}h</p>
        </div>
      </div>

      {/* Entries */}
      <div className="bg-card border border-border rounded">
        {entries.length === 0 ? (
          <div className="p-8 text-center">
            <Clock size={28} className="text-muted-foreground/30 mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">Ingen stämplingshistorik</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {entries.map((e) => (
              <div key={e.id} className="px-4 py-3" data-testid={`history-entry-${e.id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">{new Date(e.date).toLocaleDateString('sv-SE', { weekday: 'short', day: 'numeric', month: 'short' })}</p>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                      <span>
                        {e.clock_in ? fmtTime(e.clock_in) : '?'}
                        {' — '}
                        {e.clock_out ? fmtTime(e.clock_out) : 'Pågår'}
                      </span>
                      {e.break_minutes > 0 && <span>Rast: {e.break_minutes}min</span>}
                    </div>
                  </div>
                  <div className="text-right flex items-center gap-2">
                    {e.is_ob && (
                      <span className="inline-flex items-center gap-0.5 text-xs bg-purple-50 text-purple-700 px-1.5 py-0.5 rounded font-medium">
                        {e.ob_type === 'ob_night' ? <Moon size={10} /> : <Sun size={10} />}
                        OB
                      </span>
                    )}
                    {e.is_overtime && (
                      <span className="inline-flex items-center gap-0.5 text-xs bg-yellow-50 text-yellow-700 px-1.5 py-0.5 rounded font-medium">
                        <AlertTriangle size={10} /> ÖT
                      </span>
                    )}
                    <span className="text-sm font-bold">{e.total_hours ? `${e.total_hours}h` : '—'}</span>
                  </div>
                </div>
                {e.comment && <p className="text-xs text-muted-foreground mt-1">{e.comment}</p>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkerHistory;
